import {
  getFirstDefinedValue,
  getMainSectionSource,
  getSectionSource,
} from './create_sections_normalization_question_helpers.js';

function resolveSectionMainSectionId(item, section) {
  const main_section_id = getFirstDefinedValue([
    item?.main_section_id,
    item?.form_main_section_id,
    item?.section_main_section_id,
    section?.main_section_id,
    section?.form_main_section_id,
    section?.section_main_section_id,
    section?.main_section?.id,
    section?.main_section?.main_section_id,
    null,
  ]) || null;
  return main_section_id;
}

function resolveSectionId(item, section) {
  const section_id = getFirstDefinedValue([
    item?.section_id,
    item?.id,
    section?.section_id,
    section?.id,
    null,
  ]) || null;
  return section_id;
}

function resolveNormalizedSectionIds(item, section) {
  const normalized_section_ids = {
    main_section_id: resolveSectionMainSectionId(item, section),
    section_id: resolveSectionId(item, section),
  };
  return normalized_section_ids;
}

function resolveSectionName(item, section) {
  const section_name = getFirstDefinedValue([
    item?.name,
    item?.section_name,
    section?.name,
    section?.section_name,
    'Untitled Section',
  ]);
  return section_name;
}

function resolveSectionMainSectionName(item, section) {
  const main_section_name = getFirstDefinedValue([
    item?.main_section_name,
    section?.main_section_name,
    item?.form_main_section_name,
    section?.form_main_section_name,
    section?.main_section?.main_section_name,
    '',
  ]);
  return main_section_name;
}

function buildNormalizedSectionQuestions(ctx, item) {
  const raw_questions = Array.isArray(item?.questions) ? item.questions : [];
  return raw_questions.map((question_item, question_index) => {
    return ctx.normalize_question(question_item, question_index);
  });
}

function resolveSectionIdValue(section_id, fallback) {
  return section_id ?? fallback;
}

function resolveOrderNumber(item, index) {
  return Number(item?.order_number ?? index + 1);
}

function resolveSectionDescription(section) {
  return section?.description ?? '';
}

function buildNormalizedSectionIdentity(section_id, normalized_ids, index) {
  const normalized_identity = {
    id: resolveSectionIdValue(section_id, `${Date.now()}-${index}`),
    backend_id: resolveSectionIdValue(section_id, null),
    section_id: resolveSectionIdValue(section_id, null),
    main_section_id: normalized_ids.main_section_id,
  };
  return normalized_identity;
}

function buildNormalizedSectionContent(item, section, index) {
  const normalized_section_content = {
    order_number: resolveOrderNumber(item, index),
    name: resolveSectionName(item, section),
    main_section_name: resolveSectionMainSectionName(item, section),
    description: resolveSectionDescription(section),
  };
  return normalized_section_content;
}

function buildNormalizedSectionViewState(ctx, item, index) {
  const normalized_section_view_state = {
    expanded: index === 0,
    is_deleting: false,
    questions: buildNormalizedSectionQuestions(ctx, item),
  };
  return normalized_section_view_state;
}

export function buildNormalizedSection(ctx, item, index) {
  const section = getSectionSource(item);
  const normalized_ids = resolveNormalizedSectionIds(item, section);
  const section_id = normalized_ids.section_id;
  const normalized_section = {
    ...buildNormalizedSectionIdentity(section_id, normalized_ids, index),
    ...buildNormalizedSectionContent(item, section, index),
    ...buildNormalizedSectionViewState(ctx, item, index),
  };
  return normalized_section;
}

function resolveRawSections(item, main_section) {
  if (Array.isArray(item?.sub_sections)) {
    return item.sub_sections;
  }

  if (Array.isArray(main_section?.sub_sections)) {
    return main_section.sub_sections;
  }

  return [];
}

function buildSubSectionIdentity(sub_section) {
  const sub_section_identity = {
    section_id: getFirstDefinedValue([
      sub_section?.section_id,
      sub_section?.id,
      sub_section?.section?.id,
      null,
    ]) || null,
    name: getFirstDefinedValue([
      sub_section?.name,
      sub_section?.section_name,
      sub_section?.section?.name,
      '',
    ]),
  };
  return sub_section_identity;
}

function buildSubSectionMainSectionState(item, main_section) {
  const sub_section_main_section_state = {
    form_main_section_id: getFirstDefinedValue([item?.id, main_section?.id, null]) || null,
    main_section_id: getFirstDefinedValue([
      item?.main_section_id,
      main_section?.id,
      item?.id,
      null,
    ]) || null,
    main_section_name: getFirstDefinedValue([
      item?.main_section_name,
      main_section?.main_section_name,
      main_section?.name,
      '',
    ]),
  };
  return sub_section_main_section_state;
}

function buildNormalizedSubSectionInput(item, main_section, sub_section) {
  const normalized_sub_section_input = {
    ...sub_section,
    ...buildSubSectionIdentity(sub_section),
    ...buildSubSectionMainSectionState(item, main_section),
  };
  return normalized_sub_section_input;
}

function buildMainSectionIdentity(item, main_section, index) {
  const main_section_identity = {
    id: getFirstDefinedValue([item?.main_section_id, main_section?.id, item?.id, `${Date.now()}-${index}`]),
    backend_id: getFirstDefinedValue([item?.id, main_section?.id, null]) || null,
    form_main_section_id: getFirstDefinedValue([item?.id, main_section?.id, null]) || null,
    main_section_id: getFirstDefinedValue([item?.main_section_id, main_section?.id, item?.id, null]) || null,
  };
  return main_section_identity;
}

function buildMainSectionContent(item, main_section, index) {
  const main_section_content = {
    order_number: Number(item?.order_number ?? main_section?.order_number ?? index + 1),
    name: getFirstDefinedValue([
      item?.name,
      item?.main_section_name,
      main_section?.name,
      main_section?.main_section_name,
      'Untitled Main Section',
    ]),
    description: getFirstDefinedValue([
      item?.main_section_description,
      item?.description,
      main_section?.main_section_description,
      main_section?.description,
      '',
    ]),
  };
  return main_section_content;
}

function buildNormalizedMainSectionSections(ctx, item, main_section) {
  const raw_sections = resolveRawSections(item, main_section);
  return raw_sections.map((sub_section, sub_index) => {
    const sub_section_input = buildNormalizedSubSectionInput(item, main_section, sub_section);
    return ctx.normalize_section(sub_section_input, sub_index);
  });
}

export function buildNormalizedMainSection(ctx, item, index) {
  const main_section = getMainSectionSource(item);
  const normalized_main_section = {
    ...buildMainSectionIdentity(item, main_section, index),
    ...buildMainSectionContent(item, main_section, index),
    sections: buildNormalizedMainSectionSections(ctx, item, main_section),
  };
  return normalized_main_section;
}

function resolveChoiceValueFromObject(choice) {
  const object_choice_value = getFirstDefinedValue([
    choice.choice,
    choice.value,
    choice.option,
    choice.name,
    '',
  ]);
  return object_choice_value;
}

function resolveChoiceValue(choice) {
  if (choice && typeof choice === 'object') {
    return resolveChoiceValueFromObject(choice);
  }
  return choice || '';
}

export function normalizeChoiceLabel(choice) {
  const raw_choice_label = resolveChoiceValue(choice);
  const choice_label = String(raw_choice_label).trim();
  return choice_label;
}
